import { apiClient, refreshAccessToken } from '@/api/client';
import type {
  AuthUser, LoginRequest, LoginResponse, MfaRequest, TenantMembership,
} from '@/types/auth';

/**
 * Auth endpoints exposed by the identity platform behind the API gateway.
 * These are REAL endpoints; the portal never mocks them.
 */
export const authApi = {
  async login(body: LoginRequest): Promise<LoginResponse> {
    const { data } = await apiClient.post<LoginResponse>('/auth/login', {
      email: body.email,
      password: body.password,
    });
    return data;
  },

  async verifyMfa(body: MfaRequest): Promise<LoginResponse> {
    // Only the fields identity accepts (it rejects unknown fields): never the remember flag.
    const payload = body.recoveryCode
      ? { challengeId: body.challengeId, recoveryCode: body.recoveryCode }
      : { challengeId: body.challengeId, code: body.code };
    const { data } = await apiClient.post<LoginResponse>('/auth/mfa', payload);
    return data;
  },

  /** Rotates the session via the HttpOnly refresh cookie (single-flight, cross-tab safe). */
  async refresh(): Promise<{ accessToken: string }> {
    return { accessToken: await refreshAccessToken() };
  },

  async me(): Promise<AuthUser> {
    const { data } = await apiClient.get<Partial<AuthUser> & { id: string; email: string; organizationName?: string }>('/me');
    return {
      id: data.id,
      email: data.email,
      displayName: data.displayName ?? data.email,
      tenantId: data.tenantId ?? '',
      // identity /me reports the active organization as `organizationName`.
      tenantName: data.tenantName ?? data.organizationName,
      roles: data.roles ?? [],
      permissions: data.permissions ?? [],
      avatarUrl: data.avatarUrl ?? null,
    };
  },

  async logout(): Promise<void> {
    await apiClient.post('/auth/logout', {});
  },

  /** Tenants the current principal may operate as (for the tenant switcher). */
  async tenants(): Promise<TenantMembership[]> {
    const { data } = await apiClient.get<Array<{
      tenantId: string;
      organizationName: string;
      accountCode: string;
      roleKey: string;
    }> | { data: Array<{ tenantId: string; organizationName: string; accountCode: string; roleKey: string }> }>('/auth/tenants');
    const rows = Array.isArray(data) ? data : data.data;
    return rows.map((row) => ({ id: row.tenantId, name: row.organizationName, accountCode: row.accountCode, role: row.roleKey }));
  },

  /** Re-scope the session to another tenant; returns a fresh token + user. */
  async switchTenant(tenantId: string): Promise<LoginResponse> {
    const { data } = await apiClient.post<LoginResponse>('/auth/switch-tenant', { tenantId });
    return data;
  },
};
